import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Save, RotateCcw, Shuffle, Sparkles, Trash2, Home } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { JamTakeService } from "@/services/JamTakeService";
import { CognitiveSpaceAdapter } from "@/services/CognitiveSpaceAdapter";
import { MicVisualizer } from "@/components/jam/MicVisualizer";
import { useI18n } from "@/i18n/I18nContext";

export default function SessionComplete() {
  const navigate = useNavigate();
  const { pendingSession, setPendingSession, mode, setResumeTake } = useApp();
  const { t, tm, dict } = useI18n();
  const [reflection, setReflection] = useState("");
  const [saved, setSaved] = useState(false);
  const [versionName, setVersionName] = useState(dict.session.defaultVersion);

  useEffect(() => {
    if (!pendingSession) navigate("/", { replace: true });
  }, [pendingSession, navigate]);

  if (!pendingSession) return null;
  const { summary, variation } = pendingSession;

  const saveReflection = () => {
    if (!reflection.trim()) {
      toast(t("session.toastNeedWords"));
      return;
    }
    CognitiveSpaceAdapter.saveReflection(reflection.trim(), summary.songId);
    setSaved(true);
    toast.success(t("session.toastAddedSpace"));
    setTimeout(() => setSaved(false), 2200);
  };

  const persist = (progress: "Working" | "Saved" | "Fresh", varn: number) =>
    JamTakeService.save({
      songId: summary.songId,
      songTitle: summary.songTitle,
      versionName,
      mode,
      lastTime: summary.durationPlayed,
      variation: varn,
      reflection: reflection.trim() || undefined,
      summary,
      progress,
    });

  const saveVersion = () => {
    persist("Saved", variation);
    toast.success(t("session.toastSaved", { name: versionName }));
    setTimeout(() => navigate("/my-jams"), 700);
  };

  const continueTake = () => {
    const take = persist("Working", variation);
    setResumeTake(take);
    navigate(`/jam/${summary.songId}`);
  };

  const newVariation = () => {
    persist("Saved", variation + 1);
    toast.success(t("session.toastVariation"));
    setTimeout(() => navigate("/my-jams"), 700);
  };

  const freshJam = () => {
    setPendingSession(null);
    navigate(`/prepare/${summary.songId}`);
  };

  const discard = () => {
    setPendingSession(null);
    toast(t("session.toastDiscarded"));
    navigate("/");
  };

  const stats = [
    { label: dict.session.stats.flow, value: tm("flow", summary.flow) },
    { label: dict.session.stats.transitions, value: `${summary.transitionsHit} / ${summary.transitionsTotal}` },
    { label: dict.session.stats.recovery, value: `${summary.recoveries}` },
    { label: dict.session.stats.comfort, value: summary.comfortZone },
  ];

  return (
    <div className="min-h-[100dvh] bg-[#0A0C0E] text-slate-200">
      <div className="mx-auto w-full max-w-2xl px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-amber-400/90">
            {summary.songTitle}
          </span>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-100">{t("session.title")}</h1>
          <p className="mt-2 text-sm text-slate-400">{t("session.subtitle")}</p>
        </motion.div>

        {/* Summary */}
        <div data-testid="session-complete-summary" className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="rounded-2xl p-4"
              style={{ background: "rgba(18,21,25,0.8)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</div>
              <div className="mt-2 font-mono text-lg text-amber-300">{s.value}</div>
            </motion.div>
          ))}
        </div>

        {/* Reflection */}
        <div className="mt-9">
          <h2 className="text-lg font-medium text-slate-100">{t("session.feel")}</h2>
          <textarea
            data-testid="reflection-note-input"
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            placeholder={t("session.placeholder")}
            rows={3}
            className="mt-3 w-full resize-none rounded-2xl bg-transparent p-4 text-sm text-slate-200 outline-none placeholder:text-slate-600"
            style={{ background: "rgba(18,21,25,0.7)", border: "1px solid rgba(255,255,255,0.1)" }}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <MicVisualizer testId="reflection-mic-record-btn" variant="voice" />
            <button
              data-testid="save-jam-reflection-btn"
              onClick={saveReflection}
              className="relative flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold text-[#1a1204] transition active:scale-95"
              style={{ background: "linear-gradient(135deg,#FDE68A,#F59E0B)" }}
            >
              <AnimatePresence mode="wait">
                {saved ? (
                  <motion.span key="ok" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} className="flex items-center gap-2">
                    <Check size={14} /> {t("session.saved")}
                  </motion.span>
                ) : (
                  <motion.span key="add" className="flex items-center gap-2">
                    <Sparkles size={14} /> {t("session.addToSpace")}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Jam Take */}
        <div className="mt-10">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">{t("session.thisTake")}</h2>
          <input
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            className="mt-3 w-full rounded-xl bg-transparent px-4 py-2.5 text-sm text-slate-200 outline-none"
            style={{ background: "rgba(18,21,25,0.7)", border: "1px solid rgba(255,255,255,0.1)" }}
          />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <TakeBtn testId="take-continue-btn" onClick={continueTake} icon={<RotateCcw size={16} />} label={t("session.continueTake")} primary />
            <TakeBtn testId="take-save-version-btn" onClick={saveVersion} icon={<Save size={16} />} label={t("session.saveVersion")} />
            <TakeBtn testId="take-new-variation-btn" onClick={newVariation} icon={<Shuffle size={16} />} label={t("session.newVariation")} />
            <TakeBtn testId="take-fresh-jam-btn" onClick={freshJam} icon={<Sparkles size={16} />} label={t("session.freshJam")} />
          </div>
          <div className="mt-3 flex justify-between">
            <button
              data-testid="take-discard-btn"
              onClick={discard}
              className="flex items-center gap-2 rounded-full px-4 py-2 text-xs text-slate-500 transition hover:text-slate-300"
            >
              <Trash2 size={14} /> {t("session.discard")}
            </button>
            <button
              onClick={() => { setPendingSession(null); navigate("/"); }}
              className="flex items-center gap-2 rounded-full px-4 py-2 text-xs text-slate-400 transition hover:text-slate-200"
            >
              <Home size={14} /> {t("common.home")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const TakeBtn: React.FC<{
  testId: string;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  primary?: boolean;
}> = ({ testId, onClick, icon, label, primary }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-full py-3 text-xs font-semibold transition active:scale-95 ${
      primary ? "text-[#1a1204]" : "text-slate-200"
    }`}
    style={
      primary
        ? { background: "linear-gradient(135deg,#FDE68A,#F59E0B 60%,#D97706)" }
        : { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }
    }
  >
    {icon} {label}
  </button>
);
